// Reports (UI design §3.1; managers): utilisation, missing, movement history, deployment billing, tag health, requests
// (CR_20260915_Request_Qty Q12: for back-charging subcontractors outside the app) — each on screen and as CSV / Excel.
// One column list per report drives the table and both exports.
import {
  Alert,
  Button,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../auth/session';
import { Empty, Loading, LoadError, Page } from '../components/Page';
import { type BillingRow, useBilling, useProfileNames } from '../data/billing';
import { useRequests } from '../data/requests';
import { linesText, type RequestOverview } from '@fleetspoke/core/requests';
import {
  type LocationEvent,
  type TagRow,
  useLocationEvents,
  useMissingAssets,
  useTagHealth,
  useUtilisation,
  type Utilisation,
} from '../data/dashboard';
import { type ComplianceRow, useCompliance } from '../data/certificates';
import type { AssetOverview } from '../data/queries';
import {
  billingFileName,
  billingWorkbook,
  describeAdjustment,
  isoDateIn,
  periodRange,
  XLSX_TYPE,
} from '../lib/billing';
import { TAG_HEALTH_ORDER, type TagHealth, tagHealth } from '../lib/dashboard';
import { type Column, reportFileName, toCsv, toXlsx } from '../lib/exports';
import { download } from '../lib/labels';
import { isoMinuteIn, localeTag, startOfDayIso } from '../lib/time';

const REPORTS = [
  'utilisation',
  'missing',
  'movements',
  'billing',
  'tags',
  'requests',
  'compliance',
] as const;
type Report = (typeof REPORTS)[number];
type Period = 'this_month' | 'last_month' | 'last_30' | 'custom';

const HEALTH_COLOR = {
  never: 'error',
  silent_48h: 'error',
  silent_24h: 'warning',
  low_battery: 'warning',
  ok: 'success',
} as const;

function ReportTable<R>({
  rows,
  columns,
  rowKey,
  onRow,
  footer,
}: {
  rows: R[];
  columns: Column<R>[];
  rowKey(row: R): string;
  onRow?(row: R): void;
  footer?: ReactNode;
}) {
  return (
    <TableContainer>
      <Table size="small" data-testid="report-table">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.header} align={c.numFmt ? 'right' : undefined}>
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={rowKey(r)}
              hover
              sx={onRow ? { cursor: 'pointer' } : undefined}
              onClick={onRow ? () => onRow(r) : undefined}
            >
              {columns.map((c) => (
                <TableCell key={c.header} align={c.numFmt ? 'right' : undefined}>
                  {c.value(r) ?? '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {footer}
      </Table>
    </TableContainer>
  );
}

function ExportButtons({
  onCsv,
  onXlsx,
  disabled,
}: {
  onCsv(): void;
  onXlsx(): Promise<void>;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button size="small" variant="outlined" disabled={disabled} onClick={onCsv}>
        {t('reports.export_csv')}
      </Button>
      <Button
        size="small"
        variant="contained"
        disabled={disabled}
        onClick={() =>
          void onXlsx().then(
            () => setError(null),
            (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
          )
        }
      >
        {t('reports.export_xlsx')}
      </Button>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  );
}

export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const [params, setParams] = useSearchParams();
  const report: Report = REPORTS.includes(params.get('r') as Report)
    ? (params.get('r') as Report)
    : 'utilisation';
  const today = isoDateIn(tz);
  const [period, setPeriod] = useState<Period>('this_month');
  const [custom, setCustom] = useState(() => periodRange('this_month', today));
  const range =
    period === 'custom'
      ? custom
      : period === 'last_30'
        ? { from: isoDateIn(tz, new Date(Date.now() - 29 * 86_400_000)), to: today }
        : periodRange(period, today);
  const valid = !!range.from && !!range.to && range.from <= range.to;
  const nextDay = (d: string) =>
    new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

  const utilisation = useUtilisation(range.from, range.to, report === 'utilisation');
  const missing = useMissingAssets(report === 'missing');
  const movements = useLocationEvents(
    valid ? startOfDayIso(range.from, tz) : '',
    valid ? startOfDayIso(nextDay(range.to), tz) : '',
    report === 'movements' && valid,
  );
  const billing = useBilling(null, range.from, range.to, report === 'billing');
  const tags = useTagHealth(report === 'tags');
  const requests = useRequests(report === 'requests');
  const compliance = useCompliance(report === 'compliance');
  const requestRows = (requests.data ?? []).filter((r) => {
    const day = isoDateIn(tz, new Date(r.created_at));
    return day >= range.from && day <= range.to;
  });
  const people = useProfileNames(
    requestRows.flatMap((r) => [r.requested_by, r.decided_by]).filter((x): x is string => !!x),
  );
  const [healthFilter, setHealthFilter] = useState<TagHealth | ''>('');

  const when = (iso: string | null | undefined) => isoMinuteIn(iso, tz);
  const pointInTime = report === 'missing' || report === 'tags' || report === 'compliance';
  const stamp = pointInTime ? today : `${range.from}_${range.to}`;
  const heading = (title: string) => [
    `Fleetspoke Assets — ${title}`,
    me?.company?.name ?? '',
    pointInTime
      ? `${t('billing.generated')}: ${today}`
      : `${t('billing.period')}: ${range.from} – ${range.to}`,
  ];

  const utilisationCols: Column<Utilisation>[] = [
    {
      header: t('assets.category'),
      value: (r) => r.category_name || t('dashboard.no_category'),
      width: 24,
    },
    { header: t('reports.col_assets'), value: (r) => r.assets, numFmt: '0' },
    { header: t('reports.col_asset_days'), value: (r) => r.asset_days, numFmt: '0' },
    { header: t('reports.col_deployed_days'), value: (r) => r.deployed_days, numFmt: '0' },
    {
      header: t('reports.col_utilisation'),
      value: (r) => (r.utilisation == null ? null : Number(r.utilisation)),
      numFmt: '0.0',
    },
    // what the fleet is worth next to how hard it works (CR_20260920_Asset_Register §5.3)
    {
      header: t('value.col_value'),
      value: (r) => (r.value == null ? null : Number(r.value)),
      numFmt: '#,##0.00',
      width: 16,
    },
    { header: t('value.col_valued'), value: (r) => r.valued_assets ?? 0, numFmt: '0' },
  ];
  const missingCols: Column<AssetOverview>[] = [
    { header: t('billing.col_asset'), value: (r) => r.asset_code, width: 14 },
    { header: t('assets.description'), value: (r) => r.description, width: 28 },
    { header: t('assets.category'), value: (r) => r.category_name, width: 18 },
    {
      header: t('reports.col_last_site'),
      value: (r) => r.presence_site_name ?? r.custody_site_name,
      width: 22,
    },
    // where it was last put at its custody site (CR_20260914) — where to start looking
    { header: t('reports.col_zone'), value: (r) => r.zone_name, width: 16 },
    { header: t('assets.last_seen'), value: (r) => when(r.last_seen_at), width: 16 },
    { header: t('reports.col_missing_since'), value: (r) => when(r.since_at), width: 16 },
  ];
  const movementCols: Column<LocationEvent>[] = [
    { header: t('billing.col_when'), value: (r) => when(r.at), width: 16 },
    { header: t('billing.col_asset'), value: (r) => r.assets?.asset_code, width: 14 },
    {
      header: t('reports.col_event'),
      value: (r) =>
        t(`event.${r.event}`, { site: r.sites?.name ?? '', vehicle: r.vehicle_ref ?? '' }),
      width: 30,
    },
    { header: t('billing.col_site'), value: (r) => r.sites?.name, width: 22 },
    {
      header: t('reports.col_source'),
      value: (r) => (r.source ? t(`source.${r.source}`) : null),
      width: 12,
    },
  ];
  const billingCols: Column<BillingRow>[] = [
    { header: t('projects.name'), value: (r) => r.project_name, width: 24 },
    { header: t('projects.client'), value: (r) => r.client_name, width: 20 },
    { header: t('billing.col_asset'), value: (r) => r.asset_code, width: 14 },
    { header: t('assets.category'), value: (r) => r.category_name, width: 18 },
    { header: t('billing.col_site'), value: (r) => r.site_name, width: 20 },
    { header: t('billing.col_started'), value: (r) => isoDateIn(tz, new Date(r.started_at)) },
    {
      header: t('billing.col_ended'),
      value: (r) => (r.ended_at ? isoDateIn(tz, new Date(r.ended_at)) : null),
    },
    { header: t('billing.col_days'), value: (r) => r.days_billable, numFmt: '0' },
    {
      header: t('billing.rate'),
      value: (r) => (r.rate == null ? null : Number(r.rate)),
      numFmt: '#,##0.00',
    },
    { header: t('billing.currency'), value: (r) => r.currency },
    { header: t('billing.col_amount'), value: (r) => Number(r.amount), numFmt: '#,##0.00' },
  ];
  const person = (id: string | null) => (id && people.data?.get(id)) || null;
  const requestCols: Column<RequestOverview>[] = [
    { header: t('requests.col_ref'), value: (r) => r.ref, width: 10 },
    { header: t('projects.name'), value: (r) => r.project_name, width: 22 },
    { header: t('billing.col_site'), value: (r) => r.to_site_name, width: 20 },
    { header: t('requests.for_label'), value: (r) => r.requested_for, width: 20 },
    {
      header: t('requests.col_lines'),
      value: (r) => linesText({ ...r, status: 'submitted' }),
      width: 30,
    },
    { header: t('requests.col_approved'), value: (r) => r.qty_approved, numFmt: '0' },
    { header: t('requests.col_dispatched'), value: (r) => r.qty_dispatched, numFmt: '0' },
    { header: t('requests.col_received'), value: (r) => r.qty_received, numFmt: '0' },
    {
      header: t('requests.col_short'),
      value: (r) => (r.short_at ? Math.max(r.qty_approved - r.qty_dispatched, 0) : 0),
      numFmt: '0',
    },
    {
      header: t('requests.col_status'),
      value: (r) =>
        r.status === 'closed' && r.closed_reason
          ? t(`requests.closed_${r.closed_reason}`)
          : t(`requests.status_${r.status}`),
      width: 16,
    },
    { header: t('requests.col_needed_by'), value: (r) => r.needed_by, width: 12 },
    { header: t('requests.col_created'), value: (r) => when(r.created_at), width: 16 },
    { header: t('requests.col_requested_by'), value: (r) => person(r.requested_by), width: 18 },
    { header: t('requests.col_decided_by'), value: (r) => person(r.decided_by), width: 18 },
    { header: t('requests.col_closed'), value: (r) => when(r.closed_at), width: 16 },
  ];
  const tagRows = useMemo(() => {
    const now = Date.now();
    return (tags.data ?? [])
      .map((tag) => ({ ...tag, health: tagHealth(tag, now) }))
      .sort(
        (a, b) =>
          TAG_HEALTH_ORDER.indexOf(a.health) - TAG_HEALTH_ORDER.indexOf(b.health) ||
          a.identifier.localeCompare(b.identifier),
      );
  }, [tags.data]);
  const tagCols: Column<TagRow & { health: TagHealth }>[] = [
    { header: t('reports.col_tag'), value: (r) => r.identifier, width: 40 },
    { header: t('reports.col_type'), value: (r) => t(`reports.tag_type_${r.type}`), width: 14 },
    { header: t('billing.col_asset'), value: (r) => r.assets?.asset_code, width: 14 },
    { header: t('reports.col_battery'), value: (r) => r.battery_pct, numFmt: '0' },
    { header: t('assets.last_seen'), value: (r) => when(r.last_seen_at) ?? t('common.never') },
    { header: t('reports.col_health'), value: (r) => t(`reports.health_${r.health}`), width: 22 },
  ];

  // CR_20260921 §7.3: what a main contractor's HSE asks for
  const complianceCols: Column<ComplianceRow>[] = [
    { header: t('billing.col_asset'), value: (r) => r.asset_code, width: 14 },
    { header: t('assets.category'), value: (r) => r.category_name, width: 18 },
    { header: t('assets.custody_site'), value: (r) => r.site_name, width: 20 },
    { header: t('reports.col_cert_kind'), value: (r) => r.kind, width: 20 },
    { header: t('reports.col_cert_number'), value: (r) => r.number, width: 14 },
    { header: t('reports.col_cert_state'), value: (r) => t(`certs.state_${r.state}`), width: 12 },
    { header: t('reports.col_valid_until'), value: (r) => r.expires_on, width: 12 },
    { header: t('reports.col_days_left'), value: (r) => r.days_left, numFmt: '0' },
  ];

  function exportsFor<R>(name: string, title: string, rows: R[], cols: Column<R>[]) {
    return (
      <ExportButtons
        disabled={!rows.length}
        onCsv={() =>
          download(reportFileName(name, stamp, 'csv'), toCsv(rows, cols), 'text/csv;charset=utf-8')
        }
        onXlsx={async () =>
          download(
            reportFileName(name, stamp, 'xlsx'),
            await toXlsx(title, rows, cols, heading(title)),
            XLSX_TYPE,
          )
        }
      />
    );
  }

  const state = (q: { isLoading: boolean; error: unknown; data?: unknown[] }) =>
    q.isLoading ? <Loading /> : q.error ? <LoadError error={q.error} /> : null;
  const usesPeriod =
    report === 'utilisation' ||
    report === 'movements' ||
    report === 'billing' ||
    report === 'requests';

  let body: ReactNode;
  if (report === 'utilisation') {
    const rows = utilisation.data ?? [];
    body = state(utilisation) ?? (
      <>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {exportsFor('utilisation', t('reports.r_utilisation'), rows, utilisationCols)}
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows}
            columns={utilisationCols}
            rowKey={(r) => r.category_id ?? 'none'}
          />
        ) : (
          <Empty>{t('reports.none')}</Empty>
        )}
        {rows.length ? (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }} data-testid="fleet-value">
            {t('value.fleet_total', {
              amount: new Intl.NumberFormat(localeTag(i18n.language), {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(rows.reduce((n, r) => n + Number(r.value ?? 0), 0)),
              valued: rows.reduce((n, r) => n + (r.valued_assets ?? 0), 0),
              assets: rows.reduce((n, r) => n + r.assets, 0),
            })}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('reports.utilisation_help')}
        </Typography>
      </>
    );
  } else if (report === 'missing') {
    const rows = missing.data ?? [];
    body = state(missing) ?? (
      <>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {exportsFor('missing', t('reports.r_missing'), rows, missingCols)}
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows}
            columns={missingCols}
            rowKey={(r) => r.asset_id}
            onRow={(r) => void navigate(`/assets/${r.asset_id}`)}
          />
        ) : (
          <Empty>{t('reports.none_missing')}</Empty>
        )}
      </>
    );
  } else if (report === 'compliance') {
    const rows = compliance.data ?? [];
    body = state(compliance) ?? (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {t('reports.compliance_intro')}
          </Typography>
          {exportsFor('compliance', t('reports.r_compliance'), rows, complianceCols)}
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows}
            columns={complianceCols}
            rowKey={(r) => `${r.asset_id}:${r.kind}`}
            onRow={(r) => void navigate(`/assets/${r.asset_id}`)}
          />
        ) : (
          <Empty>{t('reports.compliance_empty')}</Empty>
        )}
      </>
    );
  } else if (report === 'movements') {
    const rows = movements.data ?? [];
    body = state(movements) ?? (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography color="text.secondary">
            {t('reports.movements_count', { count: rows.length })}
            {rows.length >= 5000 ? ` · ${t('reports.capped')}` : ''}
          </Typography>
          {exportsFor('movements', t('reports.r_movements'), rows, movementCols)}
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows.slice(0, 500)}
            columns={movementCols}
            rowKey={(r) => String(r.id)}
          />
        ) : (
          <Empty>{t('reports.none')}</Empty>
        )}
        {rows.length > 500 ? (
          <Typography variant="caption" color="text.secondary">
            {t('reports.first_500')}
          </Typography>
        ) : null}
      </>
    );
  } else if (report === 'billing') {
    const rows = billing.data ?? [];
    const total = Math.round(rows.reduce((s, r) => s + Number(r.amount), 0) * 100) / 100;
    body = state(billing) ?? (
      <>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <ExportButtons
            disabled={!rows.length}
            onCsv={() =>
              download(
                reportFileName('deployment-billing', stamp, 'csv'),
                toCsv(rows, billingCols),
                'text/csv;charset=utf-8',
              )
            }
            onXlsx={async () =>
              download(
                billingFileName(null, range.from, range.to),
                await billingWorkbook({
                  rows,
                  projectName: t('reports.all_projects'),
                  clientName: null,
                  from: range.from,
                  to: range.to,
                  timeZone: tz,
                  names: new Map(),
                  text: {
                    title: t('billing.export_title'),
                    project: t('projects.name'),
                    client: t('projects.client'),
                    period: t('billing.period'),
                    generated: t('billing.generated'),
                    sheet: t('billing.sheet'),
                    auditSheet: t('billing.sheet_audit'),
                    total: t('billing.total'),
                    columns: {
                      project: t('projects.name'),
                      asset: t('billing.col_asset'),
                      description: t('assets.description'),
                      category: t('assets.category'),
                      site: t('billing.col_site'),
                      started: t('billing.col_started'),
                      ended: t('billing.col_ended'),
                      expected: t('billing.expected_return'),
                      periodDays: t('billing.col_period_days'),
                      adjustmentDays: t('billing.col_adjustment_days'),
                      days: t('billing.col_days'),
                      rate: t('billing.rate'),
                      currency: t('billing.currency'),
                      amount: t('billing.col_amount'),
                      change: t('billing.col_change'),
                      reason: t('billing.reason'),
                      by: t('billing.col_by'),
                      when: t('billing.col_when'),
                    },
                    describe: (a) => describeAdjustment(a, t),
                  },
                }),
                XLSX_TYPE,
              )
            }
          />
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows}
            columns={billingCols}
            rowKey={(r) => r.deployment_id}
            onRow={(r) => void navigate(`/projects/${r.project_id}`)}
            footer={
              <TableFooter>
                <TableRow data-testid="report-total">
                  <TableCell colSpan={7}>
                    <strong>{t('billing.total')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{rows.reduce((s, r) => s + r.days_billable, 0)}</strong>
                  </TableCell>
                  <TableCell colSpan={2} />
                  <TableCell align="right">
                    <strong>{total.toFixed(2)}</strong>
                  </TableCell>
                </TableRow>
              </TableFooter>
            }
          />
        ) : (
          <Empty>{t('billing.none')}</Empty>
        )}
      </>
    );
  } else if (report === 'requests') {
    body = state(requests) ?? (
      <>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {exportsFor('requests', t('reports.r_requests'), requestRows, requestCols)}
        </Stack>
        {requestRows.length ? (
          <ReportTable
            rows={requestRows}
            columns={requestCols}
            rowKey={(r) => r.request_id}
            onRow={(r) => void navigate(`/requests/${r.request_id}`)}
          />
        ) : (
          <Empty>{t('reports.none')}</Empty>
        )}
      </>
    );
  } else {
    const rows = healthFilter ? tagRows.filter((r) => r.health === healthFilter) : tagRows;
    body = state(tags) ?? (
      <>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="health-chips">
            {TAG_HEALTH_ORDER.map((h) => {
              const n = tagRows.filter((r) => r.health === h).length;
              return (
                <Chip
                  key={h}
                  size="small"
                  color={HEALTH_COLOR[h]}
                  variant={healthFilter === h ? 'filled' : 'outlined'}
                  label={`${t(`reports.health_${h}`)} · ${n}`}
                  onClick={() => setHealthFilter(healthFilter === h ? '' : h)}
                />
              );
            })}
          </Stack>
          {exportsFor('tag-health', t('reports.r_tags'), rows, tagCols)}
        </Stack>
        {rows.length ? (
          <ReportTable
            rows={rows}
            columns={tagCols}
            rowKey={(r) => r.id}
            onRow={(r) => (r.asset_id ? void navigate(`/assets/${r.asset_id}`) : undefined)}
          />
        ) : (
          <Empty>{t('reports.none')}</Empty>
        )}
      </>
    );
  }

  return (
    <Page title={t('reports.title')}>
      <Tabs
        value={report}
        onChange={(_, v: Report) => setParams({ r: v })}
        variant="scrollable"
        sx={{ mb: 2 }}
      >
        {REPORTS.map((r) => (
          <Tab key={r} value={r} label={t(`reports.r_${r}`)} />
        ))}
      </Tabs>
      {usesPeriod ? (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2 }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(_, v: Period | null) => v && setPeriod(v)}
          >
            {(['this_month', 'last_month', 'last_30', 'custom'] as const).map((k) => (
              <ToggleButton key={k} value={k}>
                {t(k === 'last_30' ? 'reports.last_30' : `billing.period_${k}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {period === 'custom' ? (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="date"
                label={t('billing.from')}
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="date"
                label={t('billing.to')}
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          ) : (
            <Typography color="text.secondary" data-testid="report-period">
              {range.from} – {range.to}
            </Typography>
          )}
        </Stack>
      ) : null}
      {body}
    </Page>
  );
}
